import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Building2, ClipboardCheck, PoundSterling, Rocket, Sparkles, Truck } from "lucide-react";
import { useScrollToTopOnStepChange } from "@/hooks/useScrollToTopOnStepChange";

interface Props {
  open: boolean;
  onComplete: () => void;
  onStartTour: () => void;
}

const STEPS = [
  {
    icon: Sparkles,
    title: "Welcome to TransportMate",
    body: "You're a couple of minutes away from running your first job. This short guide shows what to set up first — you can leave at any point and pick up where you left off.",
  },
  {
    icon: Building2,
    title: "1. Your company",
    body: "Add your company name, address, contact details and logo. These appear on every invoice, quote and appraisal report you send.",
  },
  {
    icon: PoundSterling,
    title: "2. Getting paid",
    body: "Add your bank details and payment terms, then optionally connect QuickBooks, Xero, Sage or FreeAgent so invoices sync automatically.",
  },
  {
    icon: Truck,
    title: "3. Drivers and jobs",
    body: "Add your drivers and customers, then create your first job. Drivers receive it on the mobile app with navigation, appraisals and live tracking.",
  },
  {
    icon: ClipboardCheck,
    title: "4. Track your progress",
    body: "The Setup Centre keeps a live checklist and readiness score for your account, plus recommendations based on what you're actually using.",
  },
];

export function WelcomeWizard({ open, onComplete, onStartTour }: Props) {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  useScrollToTopOnStepChange(step);
  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  const finish = (destination?: string) => {
    onComplete();
    if (destination) navigate(destination);
  };

  // Demo isolation: real customers are never offered or joined to the Demo
  // company from onboarding. The Welcome flow always leads to the setup guide.

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onComplete(); }}>
      <DialogContent className="sm:max-w-lg" aria-describedby={undefined}>
        <div className="space-y-5">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-primary/10 p-2.5 shrink-0">
              <Icon className="h-6 w-6 text-primary" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-foreground">{current.title}</h2>
              <p className="mt-1.5 text-sm text-muted-foreground">{current.body}</p>
            </div>
          </div>

          <div>
            <Progress value={((step + 1) / STEPS.length) * 100} className="h-1.5" />
            <p className="mt-1.5 text-xs text-muted-foreground">
              Step {step + 1} of {STEPS.length}
            </p>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="ghost" size="sm" onClick={() => finish()}>
              Skip for now
            </Button>
            <div className="flex gap-2">
              {step > 0 && (
                <Button variant="outline" size="sm" onClick={() => setStep((s) => s - 1)}>
                  Back
                </Button>
              )}
              {!isLast ? (
                <Button size="sm" onClick={() => setStep((s) => s + 1)}>
                  Next
                </Button>
              ) : (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      onComplete();
                      onStartTour();
                    }}
                  >
                    Take the tour
                  </Button>
                  <Button size="sm" onClick={() => finish("/setup-centre")}>
                    <Rocket className="mr-1.5 h-4 w-4" />
                    Open Setup Centre
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default WelcomeWizard;
